<template>
    <div id="su-active-bntrx-token-management" v-observe-visibility="visibilityChanged" style="padding: 8px;height:100%;">
        <Divider orientation="left">Active BnTRx Token Management <span style="font-weight:200">({{ practisesTableData.length }} practices)</span>
            <Tooltip placement="bottom">
                <Icon type="md-help-circle" color="#4287f5" />
                <div slot="content" style="white-space: normal">
                    <p>This view lists practices with co-payment support enabled.</p>
                    <br />
                    <p>Subscription auto-renewal and activation code validity are pulled from billing per practice.</p>
                    <br />
                    <p>Fetching billing details temporarily switches practice context in the background.</p>
                </div>
            </Tooltip>
        </Divider>

        <Row :gutter="8">
            <Button @click="refreshUI" :disabled="isBillingFetchRunning" style="margin-left:8px">Refresh</Button>
            <Poptip
                confirm transfer
                width="320"
                :title="confirmFetchBillingTitle"
                ok-text="Fetch"
                cancel-text="Cancel"
                @on-ok="handleFetchBillingDetails">
                <Button type="primary" :loading="isBillingFetchRunning" :disabled="isBillingFetchRunning || practisesTableData.length == 0" style="margin-left:8px">Fetch Billing Details</Button>
            </Poptip>
            <Button @click="handleExportCsv" :disabled="practisesTableData.length == 0" style="margin-left:8px">Export CSV</Button>
            <Button @click="handleOpenBestowTokens" :disabled="isBillingFetchRunning || practisesTableData.length == 0" style="margin-left:8px">Bestow Tokens</Button>
            <span class="tipInformation" style="margin-left:8px">Showing practices with co-payment support enabled.</span>
            <span v-if="bestowSelectedPractices.length > 0" class="tipInformation" style="margin-left:8px">{{ bestowSelectedPractices.length }} practice{{ bestowSelectedPractices.length === 1 ? '' : 's' }} selected.</span>
            <span v-if="isBillingFetchRunning" class="tipInformation" style="margin-left:8px">{{ billingProgressSummary }}</span>
        </Row>

        <Row style="margin-top:8px">
            <Table style="width:100%" :height="tableHeightToFit" size="default" draggable :columns="practicesTableColumnsConfig" :data="practisesTableData" border highlight-row ref="practicesTable" row-key="practiseId" @on-selection-change="handleMainPracticeSelectionChanged" @on-select="handleMainPracticeRowSelected" @on-select-cancel="handleMainPracticeRowDeselected">
                <template slot-scope="{ row }" slot="status"> {{ row.statusCode | userFriendlyStatus }} </template>
                <template slot-scope="{ row }" slot="accountStatus"> {{ row.accountStatusCode | userFriendlyAccountStatus }} </template>
                <template slot-scope="{ row }" slot="subscriptionAutoRenewal"> {{ row.subscriptionAutoRenewal }} </template>
                <template slot-scope="{ row }" slot="activationCodeValid"> {{ row.activationCodeValid }} </template>
            </Table>
        </Row>

        <Drawer
            v-model="showBestowTokens"
            width="90"
            :mask-closable="false"
            :styles="detailsDrawerStyles">

            <p slot="header"><center>Bestow Tokens</center></p>

            <p class="tipInformation">Create a campaign, then select the practices to bestow activation codes.</p>

            <Divider orientation="left">Bestow Activation Codes</Divider>

            <p class="tipInformation" v-if="bestowCampaignCreated == null">Create the campaign to generate activation codes.</p>
            <div v-else>
                <p class="tipInformation">Campaign created: {{ bestowCampaignCreated.cName }} (ID {{ bestowCampaignCreated.cId }}).</p>
                <p class="tipInformation">Unassigned activation codes available: {{ bestowActivationCodesAvailable }}.</p>
            </div>
            <p class="tipInformation">Selected practices: {{ bestowSelectedPractices.length }}.</p>

            <Row v-if="bestowCampaignCreated != null" style="margin-top:8px">
                <Checkbox v-model="bestowOverrideExistingCodes" :disabled="bestowIsRunning">Override existing activation codes if present</Checkbox>
                <span v-if="bestowIsRunning" class="tipInformation" style="margin-left:8px">{{ bestowProgressSummary }}</span>
            </Row>

            <Row v-if="bestowResults.length > 0" style="margin-top:12px">
                <Row :gutter="8" style="margin-bottom:8px">
                    <Button type="primary" @click="handleBestowFetchBillingDetails" :loading="bestowBillingFetchRunning" :disabled="bestowBillingFetchRunning">Fetch Billing Details</Button>
                    <span v-if="bestowBillingFetchRunning" class="tipInformation" style="margin-left:8px">{{ bestowBillingProgressSummary }}</span>
                </Row>
                <Table style="width:100%" size="default" row-key="practiseId" :columns="bestowResultsTableColumnsConfig" :data="bestowResults" border></Table>
            </Row>

            <Divider orientation="left">Campaign Details</Divider>

            <Row :gutter="20">
                <Col v-bind="responsiveGrid2Sections">
                    <Card :padding="8" style="width: 100%;">
                        <p slot="title">Campaign</p>

                        <Form :label-width="150" label-position="right">
                            <FormItem label="Use Previous:">
                                <Row>
                                    <Col span="19">
                                        <Select ref="bestowCampaignDropdown"
                                            v-model="bestowPreviousCampaignId"
                                            style="width:100%"
                                            @on-change="handleBestowPreviousCampaignChanged"
                                            :remote-method="bestowLoadingFilteringCampaigns"
                                            :loading="bestowCampaignsLoadingFiltering"
                                            placeholder="Search previous campaigns"
                                            clearable filterable
                                            :disabled="bestowCampaignCreated != null">
                                            <Option v-for="item in bestowCampaignsFiltered" :value="item.cId" :key="item.cId">{{ formatBestowCampaignOption(item) }}</Option>
                                        </Select>
                                    </Col>
                                    <Col span="5">
                                        <Button type="primary" style="margin-top: 0px; margin-left: 8px" icon="md-search" @click="handleBestowSearchCampaigns" :disabled="bestowCampaignCreated != null" />
                                    </Col>
                                </Row>
                            </FormItem>
                        </Form>

                        <Form ref="bestowCampaignDetailsFormFields" :model="bestowCampaignDetails" :rules="bestowCampaignFieldRules" :label-width="150" label-position="right">
                            <FormItem label="Name:" prop="cName" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.cName" ref="bestow.cName" placeholder="Eg. Allergan - Migraine Clinics" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>

                            <FormItem label="Description:" prop="cDesc" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.cDesc" ref="bestow.cDesc" type="textarea" placeholder="Eg. Sponsoring small to medium migraine injection clinics in CA, USA" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>

                            <FormItem label="Subscription Type:" prop="sPriceId" :class="bestowMandatoryFieldsStyle">
                                <Select v-model="bestowCampaignDetails.sPriceId" ref="bestow.sPriceId" style="width:175px" :disabled="bestowCampaignCreated != null">
                                    <Option v-for="item in bestowSubscriptionTypeData" :value="item.priceID" :key="item.priceID">{{ item.name }}</Option>
                                </Select>
                            </FormItem>

                            <FormItem label="Start Date:" prop="cStartDate" :class="bestowMandatoryFieldsStyle">
                                <DatePicker v-model="bestowCampaignDetails.cStartDate" ref="bestow.cStartDate" type="date" :format="defaultDateInputFormat" :placeholder="defaultDateInputFormat.toUpperCase()" :disabled="bestowCampaignCreated != null" style="maxWidth: 200px"></DatePicker>
                            </FormItem>

                            <FormItem label="End Date:" prop="cEndDate" :class="bestowMandatoryFieldsStyle">
                                <DatePicker v-model="bestowCampaignDetails.cEndDate" ref="bestow.cEndDate" type="date" :format="defaultDateInputFormat" :placeholder="defaultDateInputFormat.toUpperCase()" :disabled="bestowCampaignCreated != null" style="maxWidth: 200px"></DatePicker>
                            </FormItem>

                            <FormItem label="Benefit Duration:">
                                <InputNumber :min="1" :max="12" v-model="bestowCampaignDetails.cBenefitDuration" :disabled="bestowCampaignCreated != null" style="width:90px"></InputNumber> months
                            </FormItem>

                            <FormItem label="No. Codes:">
                                <InputNumber :min="0" :max="1000" v-model="bestowCampaignDetails.aCodeCountRequested" :disabled="true" style="width:90px"></InputNumber> codes (auto)
                            </FormItem>

                            <FormItem label="Code Prefix:">
                                <Input v-model="bestowCampaignDetails.aCodePrefix" placeholder="Eg. SMMC" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                        </Form>
                    </Card>
                </Col>
                <Col v-bind="responsiveGrid2Sections">
                    <Card :padding="8" style="width: 100%;">
                        <p slot="title">Sponsor Details</p>

                        <Form class="bntx-patient-details-form-referrer" ref="bestowSponsorFormFields" :model="bestowCampaignDetails" :rules="bestowSponsorFieldRules" :label-width="130" label-position="right">
                            <FormItem label="Find or Create Sponsor:">
                                <Row>
                                    <Col span="18">
                                        <Select ref="bestowSponsorDropdown"
                                            v-model="bestowCampaignDetails.sponsor.sId"
                                            style="width:100%"
                                            @on-change="handleBestowSponsorChanged"
                                            :remote-method="bestowLoadingFilteringSponsors"
                                            :loading="bestowSponsorsLoadingFiltering"
                                            placeholder="Type to search, then select."
                                            clearable filterable
                                            :disabled="bestowCampaignCreated != null">
                                            <Option v-for="item in bestowSponsorsFiltered" :value="item.sId" :key="item.sId">{{ item.sName }}</Option>
                                        </Select>
                                    </Col>
                                    <Col span="3">
                                        <Button type="primary" style="margin-top: 0px; margin-left: 8px" icon="md-person-add" @click="handleBestowNewSponsor" :disabled="bestowCampaignCreated != null" />
                                    </Col>
                                    <Col span="3">
                                        <Button type="primary" style="margin-top: 0px; margin-left: 4px" icon="md-search" @click="handleBestowSearchSponsors" :disabled="bestowCampaignCreated != null" />
                                    </Col>
                                </Row>
                            </FormItem>

                            <FormItem label="Sponsor Name:" prop="sponsor.sName" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.sName" ref="bestow.sponsor.sName" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                            <FormItem label="Contact:" prop="sponsor.sContact" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.sContact" ref="bestow.sponsor.sContact" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                            <FormItem label="Email:" prop="sponsor.sEmail" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.sEmail" ref="bestow.sponsor.sEmail" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                            <FormItem label="Address:" prop="sponsor.sAddress" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.sAddress" ref="bestow.sponsor.sAddress" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                            <FormItem label="Postcode:" prop="sponsor.postcode" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.postcode" ref="bestow.sponsor.postcode" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                            <FormItem label="Country:" prop="sponsor.countryCode" :class="bestowMandatoryFieldsStyle">
                                <Select v-model="bestowCampaignDetails.sponsor.countryCode" filterable ref="bestow.sponsor.countryCode" :disabled="bestowCampaignCreated != null">
                                    <Option v-for="item in countriesRefData" :value="item.code" :key="item.code">{{ item.name }}</Option>
                                </Select>
                            </FormItem>
                            <FormItem label="Contract Ref ID:" prop="sponsor.sContractId" :class="bestowMandatoryFieldsStyle">
                                <Input v-model="bestowCampaignDetails.sponsor.sContractId" ref="bestow.sponsor.sContractId" placeholder="Eg. Allergan-2021-10" :disabled="bestowCampaignCreated != null"></Input>
                            </FormItem>
                        </Form>
                    </Card>
                </Col>
            </Row>

            <div class="drawer-footer">
                <Button style="margin-right: 8px" @click="handleCloseBestowTokens" :disabled="bestowIsRunning">Cancel</Button>
                <Button v-if="bestowCampaignCreated == null" type="primary" style="margin-right: 8px" :loading="bestowCampaignCreating" @click="handleCreateBestowCampaign" :disabled="bestowCampaignCreating || bestowSelectedPractices.length == 0">Create Campaign</Button>
                <Button v-else type="warning" style="margin-right: 8px" :loading="bestowCampaignCreating" @click="handleMakeBestowCampaignLive" :disabled="bestowCampaignCreating || bestowCampaignCreated.status == 'L'">Make Campaign Live</Button>
                <Button type="success" :loading="bestowIsRunning" @click="handleBestowTokens" :disabled="bestowCampaignCreated == null || bestowCampaignCreated.status != 'L' || bestowIsRunning">Bestow Tokens</Button>
            </div>
        </Drawer>
    </div>
</template>

<script>
import AnalyticsMgr from '@/js/AnalyticsManager.js';
const {CONFIG} = require('@/js/bntx-config')

const {itBnTx} = require('@/js/itBnTx')

export default {
    name: 'su-active-bntrx-token-management',
    components: {

    },

    created () {

    },

    destroyed() {
        window.removeEventListener('keydown', this.handleShiftKeyDown)
        window.removeEventListener('keyup', this.handleShiftKeyUp)
        window.removeEventListener('blur', this.handleShiftKeyReset)
    },

    mounted() {
        window.addEventListener('keydown', this.handleShiftKeyDown)
        window.addEventListener('keyup', this.handleShiftKeyUp)
        window.addEventListener('blur', this.handleShiftKeyReset)
    },

    data () {
            return {
                practicesTableColumnsConfig: [
                    { type: 'selection', width: 60, align: 'center' },
                    { slot: 'status',        title: 'Status',                 key: 'statusCode', resizable: true, sortable: true, width: 110 },
                    { slot: 'accountStatus', title: 'Account Status',         key: 'accountStatusCode', resizable: true, sortable: true, width: 140 },
                    {                        title: 'Practice ID',            key: 'practiseId', resizable: true, sortable: true, width: 120 },
                    {                        title: 'Practice Name',          key: 'practiseName', resizable: true, sortable: true, minWidth: 220 },
                    {                        title: 'City',                   key: 'practiseCity', resizable: true, sortable: true, width: 140 },
                    {                        title: 'Postcode',               key: 'practisePostcode', resizable: true, sortable: true, width: 110 },
                    {                        title: 'Country Code',           key: 'countryCode', resizable: true, sortable: true, width: 120 },
                    { slot: 'subscriptionAutoRenewal', title: 'Subscription auto-renewal', key: 'subscriptionAutoRenewalSort', resizable: true, width: 220, sortable: true, sortMethod: (a, b) => this.sortNullableNumber(a, b) },
                    { slot: 'activationCodeValid',  title: 'Activation code valid',  key: 'activationCodeValidSort', resizable: true, width: 200, sortable: true, sortMethod: (a, b) => this.sortNullableNumber(a, b) },
                ],

                practisesTableData: [],
                selectedPractisesForBestow: [],
                isShiftKeyDown: false,
                isApplyingShiftSelection: false,
                isShiftSelectionSyncPending: false,
                lastPracticeSelectionKey: null,
                isBillingFetchRunning: false,
                billingFetchProgress: {
                    total: 0,
                    completed: 0,
                    errors: 0,
                    currentPracticeName: ''
                },

                showBestowTokens: false,
                detailsDrawerStyles: {
                    height: 'calc(100% - 55px)',
                    overflow: 'auto',
                    paddingBottom: '53px',
                    position: 'static'
                },
                responsiveGrid2Sections : {
                    xl: 12,
                    lg: 24,
                    md: 24,
                    sm: 24,
                    xs: 24,
                },

                bestowLiveValidating: true,
                bestowLiveValidatingFieldsOK: true,
                bestowCampaignDetails: {
                    sponsor: { sName : '', sContact : '', sEmail : '', sAddress : '',
                               postcode : '', countryCode : '', sContractId : '', sId : 0 },
                    cName : '',
                    cDesc : '',
                    cStartDate : null,
                    cEndDate : null,
                    cBenefitDuration : 1,
                    aCodeCountRequested : 0,
                    aCodePrefix : '',
                    status : 'R',
                    sPriceId : '',
                    cId : undefined
                },
                bestowCampaignFieldRules : {
                    cName: [
                        { required: true, message: 'Campaign Name cannot be empty', trigger: 'blur' },
                    ],
                    sPriceId: [
                        { required: true, message: 'Subscription type must be specified', trigger: 'blur' },
                    ],
                    cStartDate: [
                        { required: true, type: 'date', message: 'Start Date is required', trigger: 'blur' },
                    ],
                    cEndDate: [
                        { required: true, type: 'date', message: 'End Date is required', trigger: 'blur' },
                    ],
                    cBenefitDuration: [
                        { required: true, type: 'integer', message: 'Benefit Duration cannot be empty', trigger: 'blur' },
                    ],
                    aCodeCountRequested: [
                        { required: true, type: 'integer', message: 'Number of Codes cannot be empty', trigger: 'blur' },
                    ],
                },
                bestowSponsorFieldRules : {
                    "sponsor.sName": [
                        { required: true, message: 'Sponsor Name cannot be empty', trigger: 'blur' },
                    ],
                    "sponsor.sEmail": [
                        { required: true, message: 'Sponsor Email cannot be empty', trigger: 'blur' },
                    ],
                    "sponsor.sContact": [
                        { required: true, message: 'Sponsor contact cannot be empty', trigger: 'blur' },
                    ],
                    "sponsor.postcode": [
                        { required: true, message: 'Postcode cannot be empty', trigger: 'blur' },
                    ],
                    "sponsor.countryCode": [
                        { required: true, message: 'Country cannot be empty', trigger: 'blur' },
                    ],
                },
                bestowSubscriptionTypeData : [],
                bestowPlans : [],
                bestowCampaignsData: [],
                bestowCampaignsFiltered: [],
                bestowCampaignsLoadingFiltering: false,
                bestowPreviousCampaignId: null,
                bestowSponsorsData: [],
                bestowSponsorsFiltered: [],
                bestowSponsorsLoadingFiltering: false,
                bestowCampaignCreating: false,
                bestowCampaignCreated: null,
                bestowActivationCodes: [],
                bestowSelectedPractices: [],
                bestowOverrideExistingCodes: false,
                bestowIsRunning: false,
                bestowProgress: {
                    total: 0,
                    completed: 0,
                    errors: 0,
                    currentPracticeName: ''
                },
                bestowBillingFetchRunning: false,
                bestowBillingProgress: {
                    total: 0,
                    completed: 0,
                    errors: 0,
                    currentPracticeName: ''
                },
                bestowResults: [],
                bestowResultsTableColumnsConfig: [
                    { title: 'Practice ID',   key: 'practiseId', width: 120 },
                    { title: 'Practice Name', key: 'practiseName', minWidth: 220 },
                    { title: 'Activation Code', key: 'activationCode', minWidth: 220 },
                    { title: 'Subscription auto-renewal', key: 'subscriptionAutoRenewal', minWidth: 200 },
                    { title: 'Activation code valid', key: 'activationCodeValid', minWidth: 170 },
                    { title: 'Status', key: 'status', width: 120 },
                    { title: 'Message', key: 'message', minWidth: 240 },
                ]
            }
        },

    filters: {

    },

    computed: {
        countriesRefData() { return CONFIG.COUNTRIES; },
        defaultDateInputFormat() {
            return this.$store.state.defaultDateInputFormat
        },
        tableHeightToFit() {
            return window.innerHeight - 140
        },
        confirmFetchBillingTitle() {
            const count = this.practisesTableData.length
            const practiceLabel = count == 1 ? 'practice' : 'practices'
            return 'Fetch billing details by switching into ' + count + ' ' + practiceLabel + '?'
        },
        billingProgressSummary() {
            const progress = this.billingFetchProgress
            const baseText = 'Fetching billing: ' + progress.completed + '/' + progress.total
            const errorText = progress.errors > 0 ? ' (' + progress.errors + ' errors)' : ''
            const currentText = progress.currentPracticeName != '' ? ' - ' + progress.currentPracticeName : ''
            return baseText + errorText + currentText
        },
        bestowMandatoryFieldsStyle() {
            return (this.bestowLiveValidating && (this.bestowLiveValidatingFieldsOK == false)) ? "revealValidationMessages" : ""
        },
        bestowActivationCodesAvailable() {
            if (!Array.isArray(this.bestowActivationCodes)) {
                return 0
            }
            return this.bestowActivationCodes.filter(code => code.status == 'U').length
        },
        bestowProgressSummary() {
            const progress = this.bestowProgress
            const baseText = 'Bestowing: ' + progress.completed + '/' + progress.total
            const errorText = progress.errors > 0 ? ' (' + progress.errors + ' errors)' : ''
            const currentText = progress.currentPracticeName != '' ? ' - ' + progress.currentPracticeName : ''
            return baseText + errorText + currentText
        },
        bestowBillingProgressSummary() {
            const progress = this.bestowBillingProgress
            const baseText = 'Fetching billing: ' + progress.completed + '/' + progress.total
            const errorText = progress.errors > 0 ? ' (' + progress.errors + ' errors)' : ''
            const currentText = progress.currentPracticeName != '' ? ' - ' + progress.currentPracticeName : ''
            return baseText + errorText + currentText
        },
    },
    methods: {

        visibilityChanged (isVisible, entry) {
            entry

            if (isVisible == true) {
                AnalyticsMgr.sendPageView('/Superuser/ActiveBnTRxTokenManagement');
                this.refreshUI()
            }
        },

        refreshUI() {
            itBnTx.getAllPractises()
                .then(response => {
                    const memoisedByPracticeId = this.$store.state.suBillingMemoByPracticeId || {}

                    this.practisesTableData = response.data
                        .filter(practise => practise.supportsCoPayments == true)
                        .map(practise => {
                            const memoised = memoisedByPracticeId[practise.practiseId]

                            return {
                                ...practise,
                                subscriptionAutoRenewal: memoised ? memoised.subscriptionAutoRenewal : 'Not fetched',
                                activationCodeValid: memoised ? memoised.activationCodeValid : 'Not fetched',
                                subscriptionAutoRenewalSort: memoised ? memoised.subscriptionAutoRenewalSort : null,
                                activationCodeValidSort: memoised ? memoised.activationCodeValidSort : null
                            }
                        })
                })
        },

        handleMainPracticeSelectionChanged(selection) {
            if (this.isApplyingShiftSelection || this.isShiftSelectionSyncPending) {
                this.syncBestowPracticeSelectionFromTable()
                return
            }

            this.syncBestowPracticeSelection(selection)
        },

        syncBestowPracticeSelectionFromTable() {
            this.$nextTick(() => {
                if (this.$refs.practicesTable && typeof this.$refs.practicesTable.getSelection == 'function') {
                    this.syncBestowPracticeSelection(this.$refs.practicesTable.getSelection())
                }
                this.isShiftSelectionSyncPending = false
            })
        },

        syncBestowPracticeSelection(selection) {
            const selected = Array.isArray(selection) ? selection : []
            this.selectedPractisesForBestow = selected
            this.bestowSelectedPractices = selected
            this.bestowCampaignDetails.aCodeCountRequested = selected.length
            if (selected.length == 0) {
                this.lastPracticeSelectionKey = null
            }
        },

        // Shift-click range selection for practices in the main table.
        handleMainPracticeRowSelected(selection, row) {
            if (this.isApplyingShiftSelection) {
                return
            }

            const selectedIndex = this.getMainPracticeRowIndex(row)
            if (selectedIndex == -1) {
                this.lastPracticeSelectionKey = null
                return
            }

            if (!this.isShiftKeyDown || this.lastPracticeSelectionKey == null) {
                this.lastPracticeSelectionKey = row.practiseId
                return
            }

            const displayRows = this.getMainPracticeDisplayRows()
            const anchorIndex = displayRows.findIndex(r => r.practiseId == this.lastPracticeSelectionKey)
            if (anchorIndex == -1) {
                this.lastPracticeSelectionKey = row.practiseId
                return
            }

            const rangeStart = Math.min(anchorIndex, selectedIndex)
            const rangeEnd = Math.max(anchorIndex, selectedIndex)
            const selectedIds = new Set((selection || []).map(practise => practise.practiseId))

            if (!this.$refs.practicesTable) {
                this.lastPracticeSelectionKey = row.practiseId
                return
            }

            this.isShiftSelectionSyncPending = true
            this.isApplyingShiftSelection = true
            try {
                for (let index = rangeStart; index <= rangeEnd; index += 1) {
                    const practise = displayRows[index]
                    if (!practise || selectedIds.has(practise.practiseId)) {
                        continue
                    }
                    this.$refs.practicesTable.toggleSelect(undefined, practise.practiseId)
                    selectedIds.add(practise.practiseId)
                }
            } finally {
                this.isApplyingShiftSelection = false
            }
            this.syncBestowPracticeSelectionFromTable()
            this.lastPracticeSelectionKey = row.practiseId
        },

        // Keep the last anchor in sync when a row is deselected.
        handleMainPracticeRowDeselected(selection, row) {
            if (this.isApplyingShiftSelection) {
                return
            }

            this.lastPracticeSelectionKey = (row && row.practiseId != null) ? row.practiseId : null
        },

        // Use the table's rendered order (after sorting/filtering) for range selection.
        getMainPracticeDisplayRows() {
            if (this.$refs.practicesTable && Array.isArray(this.$refs.practicesTable.rebuildData)) {
                return this.$refs.practicesTable.rebuildData
            }

            return this.practisesTableData
        },

        // Find the current row index for range selection logic.
        getMainPracticeRowIndex(practise) {
            if (!practise) {
                return -1
            }

            const displayRows = this.getMainPracticeDisplayRows()
            return displayRows.findIndex(row => row.practiseId == practise.practiseId)
        },

        // Track shift key state for range selection.
        handleShiftKeyDown(event) {
            if (event && event.key == 'Shift') {
                this.isShiftKeyDown = true
            }
        },

        // Track shift key state for range selection.
        handleShiftKeyUp(event) {
            if (event && event.key == 'Shift') {
                this.isShiftKeyDown = false
            }
        },

        // Reset shift state on window blur.
        handleShiftKeyReset() {
            this.isShiftKeyDown = false
        },

        async handleFetchBillingDetails() {
            if (this.isBillingFetchRunning == true) {
                return
            }

            const practises = this.practisesTableData.map(practise => ({
                practiseId: practise.practiseId,
                practiseName: practise.practiseName
            }))

            if (practises.length == 0) {
                return
            }

            const originalPractiseId = this.$store.state.loggedInDoctorDetails.practiseId

            this.isBillingFetchRunning = true
            this.billingFetchProgress = {
                total: practises.length,
                completed: 0,
                errors: 0,
                currentPracticeName: ''
            }

            this.practisesTableData = this.practisesTableData.map(practise => ({
                ...practise,
                subscriptionAutoRenewal: 'Loading...',
                activationCodeValid: 'Loading...'
            }))

            try {
                for (const practise of practises) {
                    this.billingFetchProgress.currentPracticeName = practise.practiseName || ''

                    try {
                        const memoised = this.$store.state.suBillingMemoByPracticeId[practise.practiseId]

                        if (memoised) {
                            this.updatePracticeRow(practise.practiseId, memoised)
                        } else {
                            await itBnTx.switchPractise({ inPractiseID: practise.practiseId })
                            const response = await itBnTx.getSubscriptionPlans()
                            const summaries = this.getBillingSummariesFromResponse(response.data)

                            this.updateBillingMemo(practise.practiseId, summaries)
                            this.updatePracticeRow(practise.practiseId, summaries)
                        }
                    } catch (error) {
                        this.billingFetchProgress.errors += 1
                        this.updatePracticeRow(practise.practiseId, {
                            subscriptionAutoRenewal: 'Unavailable',
                            activationCodeValid: 'Unavailable',
                            subscriptionAutoRenewalSort: null,
                            activationCodeValidSort: null
                        })
                    }

                    this.billingFetchProgress.completed += 1
                }
            } finally {
                if (originalPractiseId != null) {
                    try {
                        await itBnTx.switchPractise({ inPractiseID: originalPractiseId })
                    } catch (error) {
                        error
                    }
                }

                this.billingFetchProgress.currentPracticeName = ''
                this.isBillingFetchRunning = false
            }
        },

        updatePracticeRow(practiseId, updates) {
            const rowIndex = this.practisesTableData.findIndex(row => row.practiseId == practiseId)

            if (rowIndex == -1) {
                return
            }

            this.$set(this.practisesTableData, rowIndex, {
                ...this.practisesTableData[rowIndex],
                ...updates
            })
        },

        updateBillingMemo(practiseId, summaries) {
            const currentMemo = this.$store.state.suBillingMemoByPracticeId || {}
            this.$store.commit('suBillingMemoByPracticeId', {
                ...currentMemo,
                [practiseId]: summaries
            })
        },

        getBillingSummariesFromResponse(responseData) {
            if (responseData == null) {
                return {
                    subscriptionAutoRenewal: 'Not available',
                    activationCodeValid: 'Not available',
                    subscriptionAutoRenewalSort: null,
                    activationCodeValidSort: null
                }
            }

            const daysLeft = responseData.daysLeft
            const accountStatus = responseData.accountStatus
            const activationCodeDaysLeft = responseData.activationCodeDaysLeft

            let subscriptionAutoRenewal = 'Not available'
            let subscriptionAutoRenewalSort = null
            if (typeof daysLeft === 'number') {
                const dayLabel = Math.abs(daysLeft) == 1 ? 'day' : 'days'
                subscriptionAutoRenewalSort = daysLeft

                if (accountStatus == 'T') {
                    subscriptionAutoRenewal = daysLeft < 0
                        ? 'Trial ended'
                        : 'Trial ends in ' + daysLeft + ' ' + dayLabel
                } else if (accountStatus == 'P' || accountStatus == 'S') {
                    subscriptionAutoRenewal = daysLeft < 0
                        ? 'Overdue by ' + Math.abs(daysLeft) + ' ' + dayLabel
                        : 'Auto-renews in ' + daysLeft + ' ' + dayLabel
                } else if (accountStatus == 'C') {
                    subscriptionAutoRenewal = 'Closed'
                }
            }

            let activationCodeValid = 'Not active'
            let activationCodeValidSort = null
            if (responseData.usingActivationCode == true) {
                if (typeof activationCodeDaysLeft === 'number') {
                    const dayLabel = Math.abs(activationCodeDaysLeft) == 1 ? 'day' : 'days'
                    activationCodeValidSort = activationCodeDaysLeft
                    activationCodeValid = activationCodeDaysLeft < 0
                        ? 'Expired'
                        : 'Valid for another ' + activationCodeDaysLeft + ' ' + dayLabel
                } else {
                    activationCodeValid = 'Active'
                }
            }

            return {
                subscriptionAutoRenewal,
                activationCodeValid,
                subscriptionAutoRenewalSort,
                activationCodeValidSort
            }
        },

        sortNullableNumber(left, right) {
            const leftValue = typeof left === 'number' ? left : Number.POSITIVE_INFINITY
            const rightValue = typeof right === 'number' ? right : Number.POSITIVE_INFINITY

            if (leftValue === rightValue) {
                return 0
            }

            return leftValue > rightValue ? 1 : -1
        },

        handleOpenBestowTokens() {
            if (this.selectedPractisesForBestow.length == 0) {
                this.$Message.error('Select at least one practice before bestowing tokens.')
                return
            }

            this.resetBestowState()
            this.bestowSelectedPractices = [...this.selectedPractisesForBestow]
            this.bestowCampaignDetails.aCodeCountRequested = this.bestowSelectedPractices.length
            this.showBestowTokens = true
            this.refreshBestowCampaigns()
            this.refreshBestowSponsors()
            this.refreshBestowSubscriptionPlans()
        },

        handleCloseBestowTokens() {
            if (this.bestowIsRunning) {
                return
            }

            this.showBestowTokens = false
            this.bestowSelectedPractices = [...this.selectedPractisesForBestow]
            this.bestowCampaignDetails.aCodeCountRequested = this.bestowSelectedPractices.length
        },

        resetBestowState() {
            this.bestowLiveValidating = true
            this.bestowLiveValidatingFieldsOK = true
            this.bestowCampaignDetails = {
                sponsor: { sName : '', sContact : '', sEmail : '', sAddress : '',
                           postcode : '', countryCode : '', sContractId : '', sId : 0 },
                cName : '',
                cDesc : '',
                cStartDate : null,
                cEndDate : null,
                cBenefitDuration : 1,
                aCodeCountRequested : 0,
                aCodePrefix : '',
                status : 'R',
                sPriceId : '',
                cId : undefined
            }
            this.bestowCampaignCreating = false
            this.bestowCampaignCreated = null
            this.bestowActivationCodes = []
            this.bestowPreviousCampaignId = null
            this.bestowSelectedPractices = []
            this.bestowOverrideExistingCodes = false
            this.bestowIsRunning = false
            this.bestowProgress = {
                total: 0,
                completed: 0,
                errors: 0,
                currentPracticeName: ''
            }
            this.bestowBillingFetchRunning = false
            this.bestowBillingProgress = {
                total: 0,
                completed: 0,
                errors: 0,
                currentPracticeName: ''
            }
            this.bestowResults = []

            this.$nextTick(() => {
                if (this.$refs['bestowCampaignDetailsFormFields']) {
                    this.$refs['bestowCampaignDetailsFormFields'].resetFields()
                }
                if (this.$refs['bestowSponsorFormFields']) {
                    this.$refs['bestowSponsorFormFields'].resetFields()
                }
            })
        },

        refreshBestowCampaigns() {
            return itBnTx.getAllCampaigns()
                .then(response => {
                    this.bestowCampaignsData = (Array.isArray(response.data) ? response.data : [])
                        .sort((a, b) => {
                            const aStartDate = this.normaliseDate(a.cStartDate)
                            const bStartDate = this.normaliseDate(b.cStartDate)
                            const aDate = aStartDate == null ? 0 : aStartDate.getTime()
                            const bDate = bStartDate == null ? 0 : bStartDate.getTime()
                            if (aDate !== bDate) {
                                return bDate - aDate
                            }

                            const aName = a.cName == undefined ? '' : a.cName
                            const bName = b.cName == undefined ? '' : b.cName
                            return aName.localeCompare(bName)
                        })

                    this.bestowLoadingFilteringCampaigns("")
                })
                .catch(error => {
                    error
                    this.bestowCampaignsData = []
                    this.bestowCampaignsFiltered = []
                    this.$Message.error('Unable to load previous campaigns.')
                })
        },

        bestowLoadingFilteringCampaigns(query) {
            const normalisedQuery = (query || '').trim().toUpperCase()
            this.bestowCampaignsLoadingFiltering = true
            setTimeout(() => {
                this.bestowCampaignsFiltered = this.bestowCampaignsData
                    .filter(item => {
                        if (item.cId == this.bestowPreviousCampaignId) {
                            return true
                        }

                        if (normalisedQuery == '') {
                            return true
                        }

                        const campaignName = item.cName == undefined ? '' : item.cName
                        const sponsorName = item.sponsor == undefined || item.sponsor.sName == undefined ? '' : item.sponsor.sName
                        const codePrefix = item.aCodePrefix == undefined ? '' : item.aCodePrefix
                        return ([campaignName, sponsorName, codePrefix].join(' ')).trim().toUpperCase().indexOf(normalisedQuery) > -1
                    })
                    .slice(0, 50)
                this.bestowCampaignsLoadingFiltering = false
            }, 1)
        },

        handleBestowSearchCampaigns() {
            if (this.$refs['bestowCampaignDropdown']) {
                this.$refs['bestowCampaignDropdown'].isFocused = true
                this.$refs['bestowCampaignDropdown'].clearSingleSelect()
            }
        },

        handleBestowPreviousCampaignChanged(campaignId) {
            if (campaignId == undefined || campaignId == null || campaignId == '') {
                return
            }

            const previousCampaign = this.bestowCampaignsData.find(entry => entry.cId == campaignId)
            if (previousCampaign == undefined) {
                return
            }

            this.applyBestowPreviousCampaign(previousCampaign)
        },

        applyBestowPreviousCampaign(previousCampaign) {
            const shiftedDates = this.getShiftedCampaignDates(previousCampaign)
            const selectedCount = this.bestowSelectedPractices.length
            const sponsor = previousCampaign.sponsor || {}

            this.bestowCampaignDetails = {
                ...this.bestowCampaignDetails,
                sponsor: {
                    sName: sponsor.sName || '',
                    sContact: sponsor.sContact || '',
                    sEmail: sponsor.sEmail || '',
                    sAddress: sponsor.sAddress || '',
                    postcode: sponsor.postcode || '',
                    countryCode: sponsor.countryCode || '',
                    sContractId: sponsor.sContractId || '',
                    sId: sponsor.sId || 0,
                    status: sponsor.status || ''
                },
                cName: this.getCopiedCampaignName(previousCampaign),
                cDesc: previousCampaign.cDesc || '',
                cStartDate: shiftedDates.startDate,
                cEndDate: shiftedDates.endDate,
                cBenefitDuration: previousCampaign.cBenefitDuration || 1,
                aCodeCountRequested: selectedCount,
                aCodePrefix: previousCampaign.aCodePrefix || '',
                status: 'R',
                sPriceId: previousCampaign.sPriceId || '',
                cId: undefined
            }

            if (this.$refs['bestowSponsorDropdown']) {
                this.$refs['bestowSponsorDropdown'].setQuery(sponsor.sName || '')
                this.$refs['bestowSponsorDropdown'].isFocused = false
            }

            this.$nextTick(() => {
                this.validateBestowForm('bestowCampaignDetailsFormFields')
                this.validateBestowForm('bestowSponsorFormFields')
            })
        },

        getShiftedCampaignDates(campaign) {
            const originalStartDate = this.normaliseDate(campaign.cStartDate)
            const originalEndDate = this.normaliseDate(campaign.cEndDate)
            const today = this.getTodayAtStartOfDay()

            if (originalStartDate == null || originalEndDate == null) {
                return {
                    startDate: today,
                    endDate: today
                }
            }

            const durationInMilliseconds = Math.max(0, originalEndDate.getTime() - originalStartDate.getTime())
            return {
                startDate: today,
                endDate: new Date(today.getTime() + durationInMilliseconds)
            }
        },

        normaliseDate(value) {
            if (value == null || value == '') {
                return null
            }

            const date = value instanceof Date ? new Date(value.getTime()) : new Date(value)
            if (Number.isNaN(date.getTime())) {
                return null
            }
            return date
        },

        getTodayAtStartOfDay() {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return today
        },

        getCopiedCampaignName(campaign) {
            const campaignName = campaign.cName || 'Bestow Campaign'
            return campaignName + ' - ' + this.formatDateForCampaignName(this.getTodayAtStartOfDay())
        },

        formatDateForCampaignName(date) {
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return day + '/' + month + '/' + year
        },

        formatBestowCampaignOption(campaign) {
            const campaignName = campaign.cName || 'Unnamed campaign'
            const sponsorName = campaign.sponsor == undefined || campaign.sponsor.sName == undefined ? '' : campaign.sponsor.sName
            const startDate = this.normaliseDate(campaign.cStartDate)
            const startDateText = startDate == null ? '' : this.formatDateForCampaignName(startDate)
            const details = [sponsorName, startDateText].filter(entry => entry != '').join(' - ')
            return details == '' ? campaignName : campaignName + ' (' + details + ')'
        },

        refreshBestowSubscriptionPlans() {
            return itBnTx.getSubscriptionPlans({ inReturnAllPlans : true })
                .then(response => {
                    this.bestowPlans = response.data.plans || []
                    this.bestowSubscriptionTypeData = []

                    for (let index = 0; index < this.bestowPlans.length; index++) {
                        this.bestowSubscriptionTypeData.push({
                            priceID : this.bestowPlans[index].id,
                            name : this.bestowPlans[index].product.expandedObject.name
                        })
                    }
                })
                .catch(error => {
                    error
                    this.$Message.error('Unable to load subscription types.')
                })
        },

        refreshBestowSponsors() {
            itBnTx.getAllSponsors().then(response => {
                this.bestowSponsorsData = response.data
                    .filter(sponsor => sponsor.status == 'A')
                    .sort((a, b) => {
                        const aSponsor = a.sName == undefined ? '' : a.sName
                        const aCountryCode = a.countryCode == undefined ? '' : a.countryCode
                        const bSponsor = b.sName == undefined ? '' : b.sName
                        const bCountryCode = b.countryCode == undefined ? '' : b.countryCode

                        return (([aSponsor, aCountryCode].join(' ')).trim())
                            .localeCompare(([bSponsor, bCountryCode].join(' ')).trim())
                    })

                if ((this.bestowSponsorsData.length != 0 && this.bestowSponsorsData[0].sId != 0)
                    || this.bestowSponsorsData.length == 0) {
                    this.bestowSponsorsData.unshift({
                        sId : 0,
                        sName: '-- Create New Sponsor --'
                    })
                }

                this.bestowLoadingFilteringSponsors("")
            })
        },

        bestowLoadingFilteringSponsors(query) {
            if (query != '' || this.bestowCampaignDetails.sponsor.sId != 0) {
                this.bestowSponsorsLoadingFiltering = true
                setTimeout(() => {
                    this.bestowSponsorsFiltered = this.bestowSponsorsData.filter(item => {
                        if (item.sId == 0
                            || item.sId == this.bestowCampaignDetails.sponsor.sId) {
                            return true
                        }

                        const sponsorName = item.sName == undefined ? '' : item.sName
                        return ([sponsorName].join(' ')).trim().toUpperCase().indexOf(query.toUpperCase()) > -1
                    })
                    this.bestowSponsorsLoadingFiltering = false
                }, 1)
            } else {
                this.bestowSponsorsFiltered = [this.bestowSponsorsData[0]]
                this.bestowSponsorsLoadingFiltering = false
            }
        },

        handleBestowSponsorChanged(sponsorID) {
            if (sponsorID != undefined) {
                const newSponsor = this.bestowSponsorsData.find(entry => entry.sId == sponsorID)

                this.bestowCampaignDetails.sponsor.sId = sponsorID
                this.bestowCampaignDetails.sponsor.sName = (newSponsor != undefined && sponsorID != 0) ? newSponsor.sName : ''
                this.bestowCampaignDetails.sponsor.sEmail = (newSponsor != undefined && sponsorID != 0) ? newSponsor.sEmail : ''
                this.bestowCampaignDetails.sponsor.sAddress = (newSponsor != undefined && sponsorID != 0) ? newSponsor.sAddress : ''
                this.bestowCampaignDetails.sponsor.sContact = (newSponsor != undefined && sponsorID != 0) ? newSponsor.sContact : ''
                this.bestowCampaignDetails.sponsor.sContractId = (newSponsor != undefined && sponsorID != 0) ? newSponsor.sContractId : ''
                this.bestowCampaignDetails.sponsor.postcode = (newSponsor != undefined && sponsorID != 0) ? newSponsor.postcode : ''
                this.bestowCampaignDetails.sponsor.countryCode = (newSponsor != undefined && sponsorID != 0) ? newSponsor.countryCode : ''
                this.bestowCampaignDetails.sponsor.status = (newSponsor != undefined && sponsorID != 0) ? newSponsor.status : ''
            }
        },

        handleBestowNewSponsor() {
            if (this.$refs['bestowSponsorDropdown']) {
                this.$refs['bestowSponsorDropdown'].setQuery('-- Create New Sponsor --')
                this.$refs['bestowSponsorDropdown'].isFocused = false
            }
        },

        handleBestowSearchSponsors() {
            if (this.$refs['bestowSponsorDropdown']) {
                this.$refs['bestowSponsorDropdown'].isFocused = true
                this.$refs['bestowSponsorDropdown'].clearSingleSelect()
            }
        },

        handleCreateBestowCampaign() {
            if (this.bestowCampaignCreated != null || this.bestowCampaignCreating) {
                return
            }

            if (this.bestowSelectedPractices.length == 0) {
                this.$Message.error('Select at least one practice before creating a campaign.')
                return
            }

            this.bestowCampaignDetails.aCodeCountRequested = this.bestowSelectedPractices.length
            this.bestowLiveValidating = true

            if (!this.$refs['bestowSponsorFormFields'] || !this.$refs['bestowCampaignDetailsFormFields']) {
                return
            }

            this.$refs['bestowSponsorFormFields'].validate((sponsorValid) => {
                if (!sponsorValid) {
                    this.bestowLiveValidatingFieldsOK = false
                    this.$Message.error('Enter mandatory fields before trying again.')
                    return
                }

                this.$refs['bestowCampaignDetailsFormFields'].validate((campaignValid) => {
                    if (!campaignValid) {
                        this.bestowLiveValidatingFieldsOK = false
                        this.$Message.error('Enter mandatory fields before trying again.')
                        return
                    }

                    this.bestowLiveValidatingFieldsOK = true
                    this.bestowCampaignCreating = true

                    const savingDetails = {
                        ...this.bestowCampaignDetails,
                        sponsor: { ...this.bestowCampaignDetails.sponsor }
                    }

                    if (savingDetails.cId == undefined) {
                        delete savingDetails.cId
                    }

                    itBnTx.createCampaign({ inCampaignDetails : savingDetails })
                        .then(response => {
                            const createdCampaign = response.data
                            this.bestowCampaignCreated = createdCampaign
                            return this.handleMakeBestowCampaignLive({ inSilenceError: true })
                        })
                        .then(() => {
                            this.$Message.success('Campaign created and made Live. Activation codes are ready.')
                        })
                        .catch(error => {
                            this.$Message.error(this.getBestowErrorMessage(error))
                        })
                        .finally(() => {
                            this.bestowCampaignCreating = false
                        })
                })
            })
        },

        fetchBestowActivationCodes(campaignId) {
            if (campaignId == null) {
                return Promise.resolve()
            }

            return itBnTx.getActivationCodesForCampaignID({ inCampaignID: campaignId, inExport: false })
                .then(response => {
                    if (Array.isArray(response.data)) {
                        this.bestowActivationCodes = response.data
                    } else {
                        this.bestowActivationCodes = []
                    }
                })
                .catch(error => {
                    error
                    this.bestowActivationCodes = []
                    this.$Message.error('Unable to fetch activation codes.')
                })
        },

        handleMakeBestowCampaignLive({ inSilenceError = false } = {}) {
            if (this.bestowCampaignCreated == null) {
                return Promise.resolve()
            }

            if (this.bestowCampaignCreated.status == 'L') {
                return this.fetchBestowActivationCodes(this.bestowCampaignCreated.cId)
            }

            this.bestowCampaignCreating = true

            const makeLivePayload = {
                ...this.bestowCampaignCreated,
                status: 'L'
            }

            return itBnTx.updateCampaign({ inCampaignDetails : makeLivePayload })
                .then(updateResponse => {
                    const updatedCampaign = Array.isArray(updateResponse.data) ? updateResponse.data[0] : updateResponse.data
                    this.bestowCampaignCreated = updatedCampaign || makeLivePayload
                    const campaignId = this.bestowCampaignCreated && this.bestowCampaignCreated.cId
                        ? this.bestowCampaignCreated.cId
                        : makeLivePayload.cId
                    return this.fetchBestowActivationCodes(campaignId)
                })
                .catch(error => {
                    if (!inSilenceError) {
                        this.$Message.error(this.getBestowErrorMessage(error))
                    }
                    throw error
                })
                .finally(() => {
                    this.bestowCampaignCreating = false
                })
        },

        async handleBestowTokens() {
            if (this.bestowCampaignCreated == null) {
                this.$Message.error('Create a campaign before bestowing tokens.')
                return
            }

            if (this.bestowIsRunning) {
                return
            }

            if (this.bestowCampaignCreated.status != 'L') {
                this.$Message.error('Campaign must be Live before bestowing tokens.')
                return
            }

            const selectedPractises = this.bestowSelectedPractices || []
            if (selectedPractises.length == 0) {
                this.$Message.error('Select at least one practice to bestow tokens.')
                return
            }

            const availableCodes = this.bestowActivationCodes.filter(code => code.status == 'U')
            if (selectedPractises.length > availableCodes.length) {
                this.$Message.error('Not enough unassigned activation codes for the selected practices.')
                return
            }

            const originalPractiseId = this.$store.state.loggedInDoctorDetails.practiseId

            this.bestowIsRunning = true
            this.bestowProgress = {
                total: selectedPractises.length,
                completed: 0,
                errors: 0,
                currentPracticeName: ''
            }
            this.bestowResults = []

            try {
                for (let index = 0; index < selectedPractises.length; index++) {
                    const practise = selectedPractises[index]
                    const activationCode = availableCodes[index].aCodeId

                    this.bestowProgress.currentPracticeName = practise.practiseName || ''

                    try {
                        await itBnTx.switchPractise({ inPractiseID: practise.practiseId })
                        await itBnTx.createSubscriptionUsingActivationCode({
                            inActivationCode : activationCode,
                            inOverrideExistingActivationCodes : this.bestowOverrideExistingCodes
                        })

                        this.bestowResults.push({
                            practiseId: practise.practiseId,
                            practiseName: practise.practiseName,
                            activationCode,
                            ...this.getMemoisedBestowBilling(practise.practiseId),
                            status: 'Success',
                            message: 'Activation code applied.'
                        })
                    } catch (error) {
                        this.bestowProgress.errors += 1
                        this.bestowResults.push({
                            practiseId: practise.practiseId,
                            practiseName: practise.practiseName,
                            activationCode,
                            ...this.getMemoisedBestowBilling(practise.practiseId),
                            status: 'Failed',
                            message: this.getBestowErrorMessage(error)
                        })
                    }

                    this.bestowProgress.completed += 1
                }
            } finally {
                if (originalPractiseId != null) {
                    try {
                        await itBnTx.switchPractise({ inPractiseID: originalPractiseId })
                    } catch (error) {
                        error
                    }
                }

                this.bestowProgress.currentPracticeName = ''
                this.bestowIsRunning = false
                await this.fetchBestowActivationCodes(this.bestowCampaignCreated.cId)
            }

            if (this.bestowProgress.errors == 0) {
                this.$Message.success('Bestow complete.')
            } else {
                this.$Message.info('Bestow complete with errors. Check the results below.')
            }
        },

        getBestowErrorMessage(error) {
            if (error == null) {
                return 'Unknown error.'
            }

            if (error.responseMsg != undefined) {
                if (error.responseCode != undefined) {
                    return error.responseMsg + ' [ID=' + error.responseCode + ']'
                }
                return error.responseMsg
            }

            if (error.response && error.response.data && error.response.data.responseMsg) {
                const responseData = error.response.data
                if (responseData.responseCode != undefined) {
                    return responseData.responseMsg + ' [ID=' + responseData.responseCode + ']'
                }
                return responseData.responseMsg
            }

            if (error.message != undefined) {
                return error.message
            }

            return 'Unknown error.'
        },

        getMemoisedBestowBilling(practiseId) {
            const memoisedByPracticeId = this.$store.state.suBillingMemoByPracticeId || {}
            const memoised = memoisedByPracticeId[practiseId]

            if (!memoised) {
                return {
                    subscriptionAutoRenewal: 'Not fetched',
                    activationCodeValid: 'Not fetched'
                }
            }

            return {
                subscriptionAutoRenewal: memoised.subscriptionAutoRenewal,
                activationCodeValid: memoised.activationCodeValid
            }
        },

        updateBestowResultRow(practiseId, updates) {
            const rowIndex = this.bestowResults.findIndex(row => row.practiseId == practiseId)
            if (rowIndex == -1) {
                return
            }

            this.$set(this.bestowResults, rowIndex, {
                ...this.bestowResults[rowIndex],
                ...updates
            })
        },

        async handleBestowFetchBillingDetails() {
            if (this.bestowBillingFetchRunning) {
                return
            }

            const practices = this.bestowResults.map(result => ({
                practiseId: result.practiseId,
                practiseName: result.practiseName
            }))

            if (practices.length == 0) {
                return
            }

            const originalPractiseId = this.$store.state.loggedInDoctorDetails.practiseId

            this.bestowBillingFetchRunning = true
            this.bestowBillingProgress = {
                total: practices.length,
                completed: 0,
                errors: 0,
                currentPracticeName: ''
            }

            try {
                for (const practise of practices) {
                    this.bestowBillingProgress.currentPracticeName = practise.practiseName || ''
                    const memoised = this.$store.state.suBillingMemoByPracticeId
                        ? this.$store.state.suBillingMemoByPracticeId[practise.practiseId]
                        : null

                    if (memoised) {
                        this.updatePracticeRow(practise.practiseId, memoised)
                        this.updateBestowResultRow(practise.practiseId, {
                            subscriptionAutoRenewal: memoised.subscriptionAutoRenewal,
                            activationCodeValid: memoised.activationCodeValid
                        })
                        this.bestowBillingProgress.completed += 1
                        continue
                    }

                    try {
                        await itBnTx.switchPractise({ inPractiseID: practise.practiseId })
                        const response = await itBnTx.getSubscriptionPlans()
                        const summaries = this.getBillingSummariesFromResponse(response.data)

                        this.updateBillingMemo(practise.practiseId, summaries)
                        this.updatePracticeRow(practise.practiseId, summaries)
                        this.updateBestowResultRow(practise.practiseId, {
                            subscriptionAutoRenewal: summaries.subscriptionAutoRenewal,
                            activationCodeValid: summaries.activationCodeValid
                        })
                    } catch (error) {
                        this.bestowBillingProgress.errors += 1
                        this.updateBestowResultRow(practise.practiseId, {
                            subscriptionAutoRenewal: 'Unavailable',
                            activationCodeValid: 'Unavailable'
                        })
                    }

                    this.bestowBillingProgress.completed += 1
                }
            } finally {
                if (originalPractiseId != null) {
                    try {
                        await itBnTx.switchPractise({ inPractiseID: originalPractiseId })
                    } catch (error) {
                        error
                    }
                }

                this.bestowBillingProgress.currentPracticeName = ''
                this.bestowBillingFetchRunning = false
            }
        },

        validateBestowForm(refName) {
            if (this.bestowLiveValidating && this.showBestowTokens == true) {
                const formRef = this.$refs[refName]
                if (formRef && formRef.validate) {
                    formRef.validate((valid) => {
                        this.bestowLiveValidatingFieldsOK = valid
                    })
                }
            }
        },

        handleExportCsv() {
            if (this.practisesTableData.length == 0) {
                return
            }

            const headers = [
                'Practice ID',
                'Practice Name',
                'Status',
                'Account Status',
                'City',
                'Postcode',
                'Country Code',
                'Subscription auto-renewal',
                'Activation code valid'
            ]

            const rows = this.practisesTableData.map(practise => ([
                practise.practiseId,
                practise.practiseName,
                this.getUserFriendlyStatus(practise.statusCode),
                this.getUserFriendlyAccountStatus(practise.accountStatusCode),
                practise.practiseCity,
                practise.practisePostcode,
                practise.countryCode,
                practise.subscriptionAutoRenewal,
                practise.activationCodeValid
            ]))

            const csvContent = [headers, ...rows]
                .map(row => row.map(this.toCsvValue).join(','))
                .join('\n')

            const hiddenElement = document.createElement('a')
            hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csvContent)
            hiddenElement.target = '_blank'
            hiddenElement.download = 'active_bntrx_token_management.csv'
            hiddenElement.click()
        },

        toCsvValue(value) {
            if (value == null) {
                return '""'
            }

            const stringValue = String(value).replace(/"/g, '""')
            return '"' + stringValue + '"'
        },

        getUserFriendlyStatus(statusCode) {
            if (statusCode == 'A') return 'Active'
            if (statusCode == 'I') return 'Inactive'
            if (statusCode == 'D') return 'Deleted'
            return statusCode || ''
        },

        getUserFriendlyAccountStatus(statusCode) {
            if (statusCode == 'S') return 'Sponsored'
            if (statusCode == 'P') return 'Paid'
            if (statusCode == 'T') return 'Trial'
            if (statusCode == 'C') return 'Closed'
            return statusCode || ''
        },
    },

    watch : {
    // "propertyLevel1", "propertyLevel1.propertyLevel2"...

        "bestowCampaignDetails.cName" : function() { this.validateBestowForm('bestowCampaignDetailsFormFields') },
        "bestowCampaignDetails.sPriceId" : function() { this.validateBestowForm('bestowCampaignDetailsFormFields') },
        "bestowCampaignDetails.cStartDate" : function() { this.validateBestowForm('bestowCampaignDetailsFormFields') },
        "bestowCampaignDetails.cEndDate" : function() { this.validateBestowForm('bestowCampaignDetailsFormFields') },

        "bestowCampaignDetails.sponsor.sName" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.sContact" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.sEmail" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.sAddress" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.postcode" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.countryCode" : function() { this.validateBestowForm('bestowSponsorFormFields') },
        "bestowCampaignDetails.sponsor.sContractId" : function() { this.validateBestowForm('bestowSponsorFormFields') },

    }

}

/*
  debugging css:

  style="border: 2px solid red;"

*/
</script>

<style scoped>

.tipInformation {
    font-size: 11px;
    color: rgb(110, 110, 110);
}

.drawer-footer{
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    border-top: 1px solid #e8e8e8;
    padding: 10px 16px;
    text-align: right;
    background: #334155;
}

.revealValidationMessages {
    margin-bottom: 24px !important;
}

</style>
